<!-- <script setup lang="ts">
import { computed } from 'vue';
import { useTts } from 'src/shared/composables/useTts/useTts';
import { useQuasar } from 'quasar';
import { mdiPlay, mdiDotsVertical, mdiCog } from '@quasar/extras/mdi-v7';
import type { LanguagesShort } from 'types/Languages';

const $q = useQuasar();

interface Props {
  text?: string;
  lang?: LanguagesShort;
  showSettings?: boolean;
  showPlay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  lang: 'en',
  showSettings: true,
  showPlay: true,
});

const { pitch, rate, voices, isReady, isSpeaking, utter, pause, stop, reset } =
  useTts({ lang: props.lang });

const isVoiceSelected = computed(() => !!voice.value);
const isInnerPlay = computed(() => isSpeaking.value);

async function play() {
  if (!isReady.value || !isVoiceSelected.value || !setVoiceByText(props.text)) {
    console.warn('TTS not ready or voice not matched');
    return;
  }
  await utter(props.text);
}

function menuOpen() {
  // Optionally set a flag for menu state
}
function menuClose() {
  // Optionally set a flag for menu state
}

defineExpose({
  play,
  pause,
  stop,
  reset,
});
</script>

<template>
  <div v-if="isReady && (showSettings || showPlay)">
    <slot
      v-if="showSettings"
      name="settings"
    >
      <q-btn
        :title="$t('AUDIO_SETTINGS')"
        flat
        round
        :size="$q.screen.lt.sm ? 'sm' : 'md'"
        :icon="mdiDotsVertical"
        :disable="!isReady"
      >
        <q-menu
          style="min-width: 380px"
          @before-hide="menuClose"
          @before-show="menuOpen"
        >
          <div class="row justify-between no-wrap q-pt-md q-pa-md">
            <div class="text-h6">
              <q-icon
                :name="mdiCog"
                class="q-mr-sm"
                color="primary"
              />{{ $t('SETTINGS') }}
            </div>
            <q-btn
              v-show="isVoiceSelected && isInnerPlay"
              :title="$t('SPEAK_THE_TEXT')"
              flat
              round
              :size="$q.screen.lt.sm ? 'sm' : 'md'"
              :icon="mdiPlay"
              @click="play"
            />
          </div>
          <q-item>
            <q-select
              v-model="voice"
              :options="voices"
              option-label="name"
              option-value="self"
              emit-value
              map-options
              @update:model-value="(v) => (voice = v)"
            />
          </q-item>
          <q-item-label
            header
            class="q-pb-xs"
          >
            {{ $t('PITCH') }}
          </q-item-label>
          <q-item dense>
            <q-item-section>
              <q-slider
                v-model="pitch"
                :min="0.5"
                :max="2"
                :step="0.1"
              />
            </q-item-section>
            <q-item-section side>{{ pitch.toFixed(1) }}</q-item-section>
          </q-item>
          <q-item-label
            header
            class="q-pb-xs"
          >
            {{ $t('RATE') }}
          </q-item-label>
          <q-item dense>
            <q-item-section>
              <q-slider
                v-model="rate"
                :min="0.5"
                :max="2"
                :step="0.1"
              />
            </q-item-section>
            <q-item-section side>{{ rate.toFixed(1) }}</q-item-section>
          </q-item>
          <q-item class="reverse no-wrap q-gutter-md q-mt-sm">
            <q-btn
              flat
              @click="reset"
              >{{ $t('RESET') }}</q-btn
            >
            <q-btn
              flat
              @click="pause"
              >{{ $t('PAUSE') }}</q-btn
            >
            <q-btn
              flat
              @click="stop"
              >{{ $t('STOP') }}</q-btn
            >
          </q-item>
        </q-menu>
      </q-btn>
    </slot>
    <slot
      v-if="showPlay"
      name="play"
    >
      <q-btn
        v-show="isVoiceSelected && !isInnerPlay"
        :title="$t('SPEAK_THE_TEXT')"
        flat
        round
        :size="$q.screen.lt.sm ? 'sm' : 'md'"
        :icon="mdiPlay"
        @click="play"
      />
    </slot>
  </div>
</template> -->
<script setup lang="ts">
import { mdiVolumeHigh } from '@quasar/extras/mdi-v7';
import { useQuasar } from 'quasar';
import type { Languages } from 'types/Languages';

interface Props {
  lang?: Languages;
  squared?: boolean;
}
withDefaults(defineProps<Props>(), {
  lang: 'en-US',
  squared: false,
});
const $q = useQuasar();

// Make sure isCheckingAnswer and playQuestion are defined or imported as needed
</script>

<template>
  <q-icon
    color="grey-6"
    :class="`cursor-pointer ${squared ? '' : 'rounded-borders'} hover-bg-grey-3`"
    :name="mdiVolumeHigh"
    :size="$q.screen.gt.sm ? 'lg' : 'md'"
  />
</template>
